import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SignatureCapture from './SignatureCapture';

const ReviewSubmission = ({ 
  sections, 
  verificationSelfie, 
  damageNotes, 
  onDamageNotesChange,
  onSubmit,
  isSubmitting,
  onSignatureChange,
  signature
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const getTotalPhotos = () => {
    return sections?.reduce((total, section) => total + (section?.photos?.length || 0), 0);
  };

  const allSectionsComplete = sections?.every(
    section => section?.isNA || section?.photos?.length >= section?.requiredPhotos
  );

  const canSubmit = allSectionsComplete && verificationSelfie && acknowledged && signature;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="ClipboardCheck" size={20} color="var(--color-primary)" strokeWidth={2} />
        </div>
        <div>
          <h3 className="font-heading text-lg font-semibold text-foreground">
            Review & Submit
          </h3>
          <p className="font-body text-sm text-muted-foreground">
            Review your documentation before submitting to owner
          </p>
        </div>
      </div>

      {/* Documentation Summary */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-4 space-y-4">
        <h4 className="font-heading text-base font-semibold text-foreground">
          Documentation Summary
        </h4>
        
        {sections?.map((section) => {
          const isComplete = section?.isNA || section?.photos?.length >= section?.requiredPhotos;
          return (
            <div key={section?.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <div className="flex items-center space-x-3">
                <Icon 
                  name={isComplete ? 'CheckCircle2' : 'Circle'} 
                  size={18} 
                  className={isComplete ? 'text-success' : 'text-muted-foreground'}
                  strokeWidth={2} 
                />
                <span className="font-body text-sm text-foreground">{section?.title}</span>
              </div>
              <span className={`font-body text-sm font-semibold ${
                isComplete ? 'text-success' : 'text-muted-foreground'
              }`}>
                {section?.isNA ? 'N/A' : `${section?.photos?.length} / ${section?.requiredPhotos} photos`}
              </span>
            </div>
          );
        })}

        <div className="flex items-center justify-between py-2 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon 
              name={verificationSelfie ? 'CheckCircle2' : 'Circle'} 
              size={18} 
              className={verificationSelfie ? 'text-success' : 'text-muted-foreground'}
              strokeWidth={2} 
            />
            <span className="font-body text-sm text-foreground">Verification Selfie + ID</span>
          </div>
          <span className={`font-body text-sm font-semibold ${
            verificationSelfie ? 'text-success' : 'text-muted-foreground'
          }`}>
            {verificationSelfie ? 'Complete' : 'Required'}
          </span>
        </div>

        <div className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-3">
            <Icon 
              name={signature ? 'CheckCircle2' : 'Circle'} 
              size={18} 
              className={signature ? 'text-success' : 'text-muted-foreground'}
              strokeWidth={2} 
            />
            <span className="font-body text-sm text-foreground">Digital Signature</span>
          </div>
          <span className={`font-body text-sm font-semibold ${
            signature ? 'text-success' : 'text-muted-foreground'
          }`}>
            {signature ? 'Complete' : 'Required'}
          </span>
        </div>

        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="font-heading text-base font-semibold text-foreground">Total Photos</span>
            <span className="font-heading text-lg font-bold text-primary">
              {getTotalPhotos() + (verificationSelfie ? 1 : 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Damage Notes */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-4">
        <h4 className="font-heading text-base font-semibold text-foreground mb-3">
          Additional Notes (Optional)
        </h4>
        <textarea
          value={damageNotes}
          onChange={(e) => onDamageNotesChange(e?.target?.value)}
          placeholder="Document any pre-existing damage or concerns you noticed during inspection..."
          className="w-full min-h-[120px] px-3 py-2 border border-border rounded-md font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
      </div>

      {/* Digital Signature */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-4">
        <SignatureCapture
          onSignatureChange={onSignatureChange}
          initialSignature={signature}
        />
      </div>

      {/* Acknowledgment */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-4">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e?.target?.checked)}
            className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary cursor-pointer"
          />
          <div className="flex-1">
            <p className="font-body text-sm text-foreground">
              I confirm that I have documented the trailer condition accurately and understand that this documentation will be used to verify the trailer's condition at pickup and return. My digital signature confirms agreement with the rental terms.
            </p>
          </div>
        </label>
      </div>

      {/* Completion Status Warnings */}
      {!allSectionsComplete && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
          <div className="flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" strokeWidth={2} />
            <div className="flex-1">
              <p className="font-body text-sm text-foreground font-semibold mb-1">
                Incomplete Documentation
              </p>
              <p className="font-body text-sm text-muted-foreground">
                Please complete all required photo sections before submitting.
              </p>
            </div>
          </div>
        </div>
      )}

      {!verificationSelfie && allSectionsComplete && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
          <div className="flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" strokeWidth={2} />
            <div className="flex-1">
              <p className="font-body text-sm text-foreground font-semibold mb-1">
                Verification Required
              </p>
              <p className="font-body text-sm text-muted-foreground">
                Please capture your verification selfie with driver's license before submitting.
              </p>
            </div>
          </div>
        </div>
      )}

      {!signature && allSectionsComplete && verificationSelfie && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-md">
          <div className="flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} className="text-warning mt-0.5" strokeWidth={2} />
            <div className="flex-1">
              <p className="font-body text-sm text-foreground font-semibold mb-1">
                Signature Required
              </p>
              <p className="font-body text-sm text-muted-foreground">
                Please provide your digital signature to complete the check-in.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end space-x-3">
        <Button
          variant="primary"
          size="lg"
          onClick={onSubmit}
          disabled={!canSubmit || isSubmitting}
          iconName={isSubmitting ? 'Loader' : 'Send'}
          className="min-w-[200px]"
        >
          {isSubmitting ? 'Submitting...' : 'Submit to Owner'}
        </Button>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-md">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" strokeWidth={2} />
          <div className="flex-1">
            <p className="font-body text-sm text-foreground font-semibold mb-2">
              What happens next?
            </p>
            <ul className="font-body text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Owner will be notified of your completed check-in</li>
              <li>Documentation will be saved for rental records</li>
              <li>Your signature confirms the pre-rental condition</li>
              <li>Same process required at return for comparison</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmission;